/**
 * VendorOnboardingPrompt - Shown to vendor-track users with no claimed entities.
 * Primary path: directs user to find their entity on the map and self-claim.
 * Secondary path: contact form escape hatch for edge cases.
 * @atomic @idempotent
 */

import { memo } from 'react'
import { TruckIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

const VendorOnboardingPrompt = memo(function VendorOnboardingPrompt({ onOpenContact }) {
  const { t } = useTranslation()

  const handleCta = () => {
    onOpenContact?.({ intent: 'claim', isVendor: true })
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-10 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center mb-4">
        <TruckIcon className="h-8 w-8 text-primary" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-bold text-gray-900 mb-2">
        {t('vendor.noEntity.title', 'Add your listing')}
      </h2>

      {/* Primary hint: self-service map flow */}
      <div className="flex items-start gap-2 text-left bg-primary/5 border border-primary/15 rounded-xl px-4 py-3 mb-5 max-w-xs">
        <MapPinIcon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-gray-700">
          {t('vendor.noEntity.description', 'Find your entity on the map and click "Become a Steward" to claim it instantly.')}
        </p>
      </div>

      {/* Secondary CTA: contact form escape hatch */}
      <button
        type="button"
        onClick={handleCta}
        onTouchEnd={(e) => { e.preventDefault(); handleCta() }}
        className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
      >
        {t('vendor.noEntity.cta', "Can't find it? Request vendor access")}
      </button>
    </div>
  )
})

export default VendorOnboardingPrompt
